import { DocumentType, Media, UploadType } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { ReactElement } from 'react';
import {
    customerMediaSelector,
    internalMediaSelector,
    removeUploadedFile,
    updateUploadedFileTitle,
    uploadFile,
} from '@synergy/data-access';
import { useParams } from 'react-router-dom';

import { DemandSteps } from '../models/demand-steps';
import { PaloAttachments } from '../../../../../common/components/palo/palo-attachments';
import { demandDetailStepperStateSelector } from '../store/demand-detail-selectors';
import { noticeUploaded } from '../../../../../common/utils/upload-notice';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

/**
 * The files of a demand, for the dock's third tab.
 *
 * A demand keeps two sets that must not run into each other: what goes out to
 * the members with the remark, and what stays inside. They are two groups under
 * one tab, which is where the study puts a document's files.
 */
export const PaloDemandAttachments = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const internal = useAppSelector(internalMediaSelector);
    const customer = useAppSelector(customerMediaSelector);
    const downloadBaseUrl = useAppSelector(state => state.settings.mediaBaseUrl);
    const params = useParams<{ id: string }>();
    const documentId = Number(params.id);
    const readonly = useAppSelector(demandDetailStepperStateSelector).readonlySteps.includes(DemandSteps.Overview);

    const drop =
        (type: UploadType, existing: Media[]) =>
        (files: File[], titles: string[] = []) => {
            const deduplicated = files
                .map((file, index) => ({ file, title: titles[index] }))
                .filter(
                    ({ file }) => !existing.some(media => media.originalName === file.name && media.size === file.size)
                );

            if (documentId && deduplicated.length > 0) {
                void noticeUploaded(
                    dispatch,
                    deduplicated.map(({ file, title }) =>
                        dispatch(uploadFile(DocumentType.Demand, documentId, file, type, title))
                    )
                );
            }
        };

    const remove = (media: Media) => {
        if (documentId) {
            dispatch(removeUploadedFile(DocumentType.Demand, documentId, media));
        }
    };

    const rename = (media: Media, title: string) => dispatch(updateUploadedFileTitle(media, title));

    const cancel = (media: Media) => media.cancel?.();

    return (
        <PaloAttachments
            groups={[
                {
                    key: 'customer',
                    label: <FormattedMessage id="app.attachments.group.customer" />,
                    files: customer,
                    downloadBaseUrl,
                    onDrop: readonly ? undefined : drop(UploadType.Customer, customer),
                    onRemove: readonly ? undefined : remove,
                    onRename: readonly ? undefined : rename,
                    onCancel: cancel,
                    readOnly: readonly,
                },
                {
                    key: 'internal',
                    label: <FormattedMessage id="app.attachments.group.internal" />,
                    files: internal,
                    downloadBaseUrl,
                    onDrop: drop(UploadType.Internal, internal),
                    onRemove: remove,
                    onRename: rename,
                    onCancel: cancel,
                },
            ]}
        />
    );
};
